// Main-thread proxy for the slicer Web Worker.
//
// Two paths:
//  1) Tek-worker fallback: tek bir kalıcı worker, request/response by id.
//     Küçük mesh'ler (~< 30k tri) için worker pool overhead'i gain'i geçer.
//  2) Worker pool (default for larger meshes): main thread bucket index'i
//     bir kez kurar, layer'ları core sayısına böler, her worker kendi
//     parçasını paralel slice eder, sonuç merge edilir + g-code tek
//     worker'da üretilir (state'li).
//
// PERFORMANCE: 256k tri × 200 layer örnek vase için ölçüm hedefi:
//   - Eski tek worker, no bucket: ~3-4 sn
//   - Yeni tek worker + bucket:    ~80-200 ms (algorithmic ~30-50× win)
//   - 4-core worker pool + bucket: ~30-60 ms (paralel ~3× ek)
import type { SliceJob, SliceResult, GCodeOutput, Layer } from '../core/types';
import { buildBucketIndex } from '../core/slicer';

interface PendingRequest {
  resolve: (r: { sliceResult: SliceResult; gcodeOutput: GCodeOutput }) => void;
  reject: (e: Error) => void;
}

interface WorkerSlot {
  worker: Worker;
  busy: boolean;
}

const MIN_TRIS_FOR_POOL = 8000;
const POOL_SIZE = Math.max(2, Math.min(8, (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4));

// Persistent single worker for the legacy/g-code path.
let primaryWorker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, PendingRequest>();
const partialPending = new Map<number, (m: { ok: boolean; layers?: Layer[]; error?: string }) => void>();

let pool: WorkerSlot[] | null = null;

function newWorker(): Worker {
  const w = new Worker(new URL('./slicerWorker.ts', import.meta.url), { type: 'module' });
  w.onmessage = (e: MessageEvent) => {
    const { id, ok, sliceResult, gcodeOutput, layers, error } = e.data;
    // Range responses go to partialPending (worker pool), full responses go to pending.
    const part = partialPending.get(id);
    if (part) { partialPending.delete(id); part({ ok, layers, error }); return; }
    const p = pending.get(id);
    if (!p) return;
    pending.delete(id);
    if (ok) p.resolve({ sliceResult, gcodeOutput });
    else p.reject(new Error(error ?? 'worker failed'));
  };
  w.onerror = (e) => {
    console.error('slicer worker crashed', e);
    for (const p of pending.values()) p.reject(new Error(e.message ?? 'worker crashed'));
    pending.clear();
    partialPending.clear();
  };
  return w;
}

function getPrimary(): Worker {
  if (primaryWorker) return primaryWorker;
  primaryWorker = newWorker();
  return primaryWorker;
}

function getPool(): WorkerSlot[] {
  if (pool) return pool;
  pool = Array.from({ length: POOL_SIZE }, () => ({ worker: newWorker(), busy: false }));
  return pool;
}

/** Single-shot range slice on a specific worker — returns its layers. */
function runRange(
  slot: WorkerSlot,
  triangles: SliceJob['triangles'],
  index: ReturnType<typeof buildBucketIndex>,
  layerStart: number,
  layerEnd: number
): Promise<Layer[]> {
  return new Promise((resolve, reject) => {
    const id = nextId++;
    slot.busy = true;
    partialPending.set(id, (m) => {
      slot.busy = false;
      if (m.ok && m.layers) resolve(m.layers);
      else reject(new Error(m.error ?? 'range slice failed'));
    });
    slot.worker.postMessage({ id, cmd: 'sliceRange', triangles, index, layerStart, layerEnd });
  });
}

/** Public API — automatically picks single-worker vs pool based on mesh size. */
export async function sliceInWorker(job: SliceJob): Promise<{ sliceResult: SliceResult; gcodeOutput: GCodeOutput }> {
  // Small mesh → keep the legacy single-worker path (avoid pool spin-up cost).
  if (job.triangles.length < MIN_TRIS_FOR_POOL) {
    return sliceSingle(job);
  }
  return slicePooled(job);
}

function sliceSingle(job: SliceJob): Promise<{ sliceResult: SliceResult; gcodeOutput: GCodeOutput }> {
  const w = getPrimary();
  const id = nextId++;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ id, cmd: 'slice', job });
  });
}

async function slicePooled(job: SliceJob): Promise<{ sliceResult: SliceResult; gcodeOutput: GCodeOutput }> {
  // 1) Build bucket index on main thread — fast, single pass.
  const index = buildBucketIndex(job.triangles, job.settings.layerHeight, job.settings.firstLayerHeight);
  const L = index.zHeights.length;

  if (L === 0) {
    // Empty mesh — fall through to single worker so g-code path produces a valid empty result.
    return sliceSingle(job);
  }

  // 2) Partition layers across pool. Each worker gets a contiguous range.
  const slots = getPool();
  const chunkSize = Math.ceil(L / slots.length);
  const ranges: { start: number; end: number; slot: WorkerSlot }[] = [];
  for (let i = 0; i < slots.length; i++) {
    const start = i * chunkSize;
    if (start >= L) break;
    const end = Math.min(L, start + chunkSize);
    ranges.push({ start, end, slot: slots[i] });
  }

  // 3) Dispatch in parallel. Workers each receive (full triangles, full index, range).
  //    Triangles are structured-cloned to each worker — the cost (~5-15ms per
  //    MB on V8) is amortized across the parallel slice work that follows.
  const partials = await Promise.all(ranges.map((r) => runRange(r.slot, job.triangles, index, r.start, r.end)));

  // 4) Merge partials in original layer order.
  const layers: Layer[] = new Array(L);
  for (let i = 0; i < ranges.length; i++) {
    const p = partials[i];
    for (let li = 0; li < p.length; li++) layers[ranges[i].start + li] = p[li];
  }
  const sliceResult: SliceResult = { layers, meshBounds: index.bounds };

  // 5) G-code generation on primary worker (sequential extruder state — not
  //    safely parallelizable). Avoids re-slicing inside the worker by piggy-
  //    backing the already-sliced result.
  const w = getPrimary();
  const gid = nextId++;
  const gcodeOutput = await new Promise<GCodeOutput>((resolve, reject) => {
    pending.set(gid, {
      resolve: (r) => resolve(r.gcodeOutput),
      reject
    });
    w.postMessage({ id: gid, cmd: 'gcode', job, sliceResult });
  });

  return { sliceResult, gcodeOutput };
}
